import {
  AddRounded,
  ClearRounded,
  CloseRounded,
  DescriptionRounded,
  EditRounded,
  LaunchRounded,
  PreviewRounded,
  SearchRounded,
  SaveRounded,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import QuestionnaireEmbedFrame from "../components/questionnaires/QuestionnaireEmbedFrame";
import { FORM_ADMIN_URL, FORM_USER_URL } from "../config/config";
import { Plan } from "../data/models/Plan/PlanModel";
import {
  Questionnaire,
  QuestionnaireVersion,
} from "../data/models/Questionnaire/QuestionnaireModel";
import { useAdminQuestionnairesPage } from "./hooks/useAdminQuestionnairesPage";

const EMPTY_SCHEMA = {
  display: "wizard",
  components: [],
};

type QuestionnaireTab = "general" | "versions" | "plans";

const versionLabel = (version?: QuestionnaireVersion | null) =>
  version ? `v${version.versionNumber}` : "Sin versión";

const statusColor = (
  status: QuestionnaireVersion["status"] | Questionnaire["status"],
): "default" | "success" | "warning" | "error" => {
  switch (status) {
    case "PUBLISHED":
    case "ACTIVE":
      return "success";
    case "DRAFT":
      return "warning";
    case "DISABLED":
      return "error";
    default:
      return "default";
  }
};

const pageCardSx = {
  borderRadius: 3,
  border: "1px solid rgba(15, 23, 42, 0.10)",
  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
};

const normalizeSearchText = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export default function AdminQuestionnairesPage() {
  const {
    questionnaires,
    plans,
    loading,
    saving,
    selectedQuestionnaire,
    selectedVersion,
    setSelectedQuestionnaire,
    setVersion,
    createQuestionnaire,
    createVersion,
    publishVersion,
    assignPlans,
    saveQuestionnaireDetails,
    applySavedVersionSchema,
  } = useAdminQuestionnairesPage();

  const [createOpen, setCreateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderVersionId, setBuilderVersionId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<QuestionnaireTab>("general");
  const [questionnaireName, setQuestionnaireName] = useState("");
  const [questionnaireCode, setQuestionnaireCode] = useState("");
  const [questionnaireDescription, setQuestionnaireDescription] = useState("");
  const [selectedPlanCodes, setSelectedPlanCodes] = useState<string[]>([]);
  const [editorName, setEditorName] = useState("");
  const [editorCode, setEditorCode] = useState("");
  const [editorDescription, setEditorDescription] = useState("");
  const [editorStatus, setEditorStatus] =
    useState<Questionnaire["status"]>("ACTIVE");
  const [editorPlanCodes, setEditorPlanCodes] = useState<string[]>([]);
  const [questionnaireSearch, setQuestionnaireSearch] = useState("");
  const [questionnaireStatusFilter, setQuestionnaireStatusFilter] = useState<
    Questionnaire["status"] | "ALL"
  >("ALL");

  const planOptions = useMemo(() => plans ?? [], [plans]);
  const selectedQuestionnaireDetail = selectedQuestionnaire;
  const questionnaireVersions = selectedQuestionnaireDetail?.versions ?? [];
  const selectedVersionSchema = selectedVersion?.schema ?? EMPTY_SCHEMA;
  const isDraftVersion = selectedVersion?.status === "DRAFT";
  const builderVersion =
    questionnaireVersions.find((version) => version.id === builderVersionId) ??
    selectedVersion;
  const activeBuilderVersion = builderVersion ?? selectedVersion;
  const currentSelectedPlanCodes = useMemo(
    () =>
      selectedQuestionnaire?.planAssignments?.map(
        (assignment) => assignment.planCode,
      ) ?? [],
    [selectedQuestionnaire],
  );
  const filteredQuestionnaires = useMemo(() => {
    const query = normalizeSearchText(questionnaireSearch);

    return questionnaires.filter((questionnaire) => {
      if (
        questionnaireStatusFilter !== "ALL" &&
        questionnaire.status !== questionnaireStatusFilter
      ) {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchableText = normalizeSearchText(
        [
          questionnaire.name,
          questionnaire.code,
          questionnaire.description ?? "",
          questionnaire.status,
          questionnaire.versions
            .map(
              (version) => `version ${version.versionNumber} ${version.status}`,
            )
            .join(" "),
          questionnaire.planAssignments
            .map((assignment) => assignment.plan?.name ?? assignment.planCode)
            .join(" "),
        ].join(" "),
      );

      return searchableText.includes(query);
    });
  }, [questionnaires, questionnaireSearch, questionnaireStatusFilter]);

  const clearQuestionnaireFilters = () => {
    setQuestionnaireSearch("");
    setQuestionnaireStatusFilter("ALL");
  };

  useEffect(() => {
    if (!selectedQuestionnaireDetail) return;
    setEditorName(selectedQuestionnaireDetail.name);
    setEditorCode(selectedQuestionnaireDetail.code);
    setEditorDescription(selectedQuestionnaireDetail.description || "");
    setEditorStatus(selectedQuestionnaireDetail.status);
    setEditorPlanCodes(currentSelectedPlanCodes);
  }, [selectedQuestionnaireDetail, currentSelectedPlanCodes]);

  const handleOpenEditor = (questionnaireId: string) => {
    setSelectedQuestionnaire(questionnaireId);
    setBuilderOpen(false);
    setBuilderVersionId(null);
    setPreviewOpen(false);
    setEditorOpen(true);
    setActiveTab("general");
  };

  const handleCloseEditor = () => {
    setBuilderOpen(false);
    setBuilderVersionId(null);
    setEditorOpen(false);
    setPreviewOpen(false);
    setActiveTab("general");
  };

  const handleOpenBuilder = async () => {
    if (!selectedVersion) return;

    if (!isDraftVersion) {
      await handleCreateDraftFromVersion(selectedVersion);
      return;
    }

    setBuilderVersionId(selectedVersion.id);
    setBuilderOpen(true);
    setPreviewOpen(false);
  };

  const handleCreateDraftFromVersion = async (version: QuestionnaireVersion) => {
    if (!selectedQuestionnaireDetail) return;

    const created = await createVersion({
      questionnaireId: Number(selectedQuestionnaireDetail.id),
      sourceVersionId: Number(version.id),
      schema: version.schema,
      notes: version.notes ?? undefined,
    });

    if (created) {
      setBuilderVersionId(created.id);
      setBuilderOpen(true);
      setPreviewOpen(false);
      setActiveTab("general");
    }
  };

  const handleEditVersion = async (version: QuestionnaireVersion) => {
    if (version.status === "DRAFT") {
      setVersion(version.id);
      setBuilderVersionId(version.id);
      setBuilderOpen(true);
      setPreviewOpen(false);
      setActiveTab("general");
      return;
    }

    await handleCreateDraftFromVersion(version);
  };

  const handleCreate = async () => {
    if (!questionnaireName.trim()) return;
    const created = await createQuestionnaire({
      name: questionnaireName,
      code: questionnaireCode || undefined,
      description: questionnaireDescription || undefined,
      schema: EMPTY_SCHEMA,
      planCodes: selectedPlanCodes,
    });
    if (created) {
      setCreateOpen(false);
      setQuestionnaireName("");
      setQuestionnaireCode("");
      setQuestionnaireDescription("");
      setSelectedPlanCodes([]);
      setEditorOpen(true);
      setActiveTab("general");
    }
  };

  const handleSaveSelectedQuestionnaire = async () => {
    if (!selectedQuestionnaire) return;
    await saveQuestionnaireDetails({
      questionnaireId: Number(selectedQuestionnaire.id),
      code: editorCode,
      name: editorName,
      description: editorDescription || undefined,
      status: editorStatus,
      planCodes: editorPlanCodes,
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "55vh",
          display: "grid",
          placeItems: "center",
          p: 3,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Cargando cuestionarios...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <>
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        m: { xs: 2, md: 3 },
        width: "auto",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, md: 3 },
          "&:last-child": { pb: { xs: 2, md: 3 } },
        }}
      >
        <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} color="text.primary">
            Cuestionarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona formularios, versiones y planes desde un panel centralizado.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="large"
          startIcon={<AddRounded />}
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: 3,
            px: 2.5,
            py: 1.1,
            boxShadow: `0 16px 28px ${alpha("#032B66", 0.22)}`,
          }}
        >
          Nuevo cuestionario
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: "1px solid rgba(15, 23, 42, 0.08)",
          bgcolor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <TextField
              value={questionnaireSearch}
              onChange={(event) => setQuestionnaireSearch(event.target.value)}
              placeholder="Buscar por nombre, código, plan o versión"
              fullWidth
              size="small"
              sx={{ maxWidth: { md: 520 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: questionnaireSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Limpiar búsqueda"
                      onClick={() => setQuestionnaireSearch("")}
                    >
                      <ClearRounded fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
            >
              {(["ALL", "ACTIVE", "INACTIVE"] as const).map((filterValue) => {
                const label =
                  filterValue === "ALL"
                    ? "Todos"
                    : filterValue === "ACTIVE"
                      ? "Activos"
                      : "Inactivos";

                return (
                  <Chip
                    key={filterValue}
                    label={label}
                    onClick={() => setQuestionnaireStatusFilter(filterValue)}
                    variant={
                      questionnaireStatusFilter === filterValue
                        ? "filled"
                        : "outlined"
                    }
                    color={
                      questionnaireStatusFilter === filterValue
                        ? "primary"
                        : "default"
                    }
                    sx={{ borderRadius: 999, cursor: "pointer" }}
                  />
                );
              })}
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Mostrando {filteredQuestionnaires.length} de {questionnaires.length}{" "}
              cuestionarios
            </Typography>

            {(questionnaireSearch || questionnaireStatusFilter !== "ALL") && (
              <Button size="small" onClick={clearQuestionnaireFilters}>
                Limpiar filtros
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {filteredQuestionnaires.length === 0 ? (
        <Alert severity="info">
          No encontramos cuestionarios con ese filtro. Prueba buscando por
          nombre, código o plan.
        </Alert>
      ) : null}

      <Grid container spacing={2.5} sx={{ width: "100%", m: 0 }}>
        {filteredQuestionnaires.map((questionnaire) => {
          const isSelected =
            questionnaire.id === selectedQuestionnaireDetail?.id &&
            (editorOpen || builderOpen || previewOpen);
          const currentVersion =
            questionnaire.activeVersion ?? questionnaire.versions?.[0] ?? null;
          const topPlanAssignments = questionnaire.planAssignments.slice(0, 3);

          return (
            <Grid item xs={12} sm={6} lg={4} key={questionnaire.id} sx={{ minWidth: 0 }}>
              <Card
                onClick={() => handleOpenEditor(questionnaire.id)}
                sx={{
                  ...pageCardSx,
                  height: "100%",
                  minHeight: 218,
                  cursor: "pointer",
                  bgcolor: isSelected ? "#f8fbff" : "background.paper",
                  borderColor: isSelected
                    ? "primary.main"
                    : "rgba(15, 23, 42, 0.12)",
                  boxShadow: isSelected
                    ? `0 18px 42px ${alpha("#032B66", 0.16)}`
                    : pageCardSx.boxShadow,
                  transition: "transform 0.18s ease, box-shadow 0.18s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 20px 44px ${alpha("#032B66", 0.12)}`,
                  },
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2.5,
                        display: "grid",
                        placeItems: "center",
                        color: isSelected ? "primary.main" : "text.secondary",
                        bgcolor: alpha("#032B66", 0.08),
                      }}
                    >
                      <DescriptionRounded />
                    </Box>
                    <Chip
                      label={questionnaire.status}
                      size="small"
                      color={statusColor(questionnaire.status)}
                      variant={questionnaire.status === "ACTIVE" ? "filled" : "outlined"}
                      sx={{ flexShrink: 0 }}
                    />
                  </Stack>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                      {questionnaire.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 42,
                      }}
                    >
                      {questionnaire.description || "Prueba de flujo estándar para validación inicial."}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {topPlanAssignments.length > 0 ? (
                      topPlanAssignments.map((assignment) => (
                        <Chip
                          key={`${questionnaire.id}-${assignment.planCode}`}
                          label={assignment.plan?.name ?? assignment.planCode}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 999,
                            maxWidth: "100%",
                            "& .MuiChip-label": {
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Chip
                        label="Sin planes"
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 999 }}
                      />
                    )}
                  </Stack>

                  <Box sx={{ mt: "auto" }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Chip
                        label={versionLabel(currentVersion)}
                        size="small"
                        color={currentVersion ? statusColor(currentVersion.status) : "default"}
                        variant="outlined"
                        sx={{ borderRadius: 999 }}
                      />
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenEditor(questionnaire.id);
                        }}
                        sx={{
                          color: "primary.main",
                          bgcolor: alpha("#032B66", 0.06),
                          "&:hover": {
                            bgcolor: alpha("#032B66", 0.12),
                          },
                        }}
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.75, display: "block" }}
                    >
                      {questionnaire.code}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        <Grid item xs={12} sm={6} lg={4} sx={{ minWidth: 0 }}>
          <Card
            onClick={() => setCreateOpen(true)}
            sx={{
              ...pageCardSx,
              minHeight: 218,
              borderStyle: "dashed",
              bgcolor: "transparent",
              color: "text.secondary",
              cursor: "pointer",
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.18s ease, border-color 0.18s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                borderColor: "primary.main",
                bgcolor: alpha("#032B66", 0.03),
              },
            }}
          >
            <Stack alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  border: "1px dashed",
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: alpha("#032B66", 0.05),
                }}
              >
                <AddRounded />
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Añadir nueva plantilla
              </Typography>
              <Typography variant="body2" textAlign="center">
                Crea un cuestionario desde cero o parte de una versión existente.
              </Typography>
            </Stack>
          </Card>
        </Grid>
      </Grid>
        </Stack>
      </CardContent>
    </Card>

      <Drawer
        anchor="right"
        open={editorOpen && Boolean(selectedQuestionnaireDetail)}
        onClose={handleCloseEditor}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(5, 10, 24, 0.42)",
              backdropFilter: "blur(10px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: 780 },
            maxWidth: "100%",
            bgcolor: "#f7faff",
            borderTopLeftRadius: { xs: 0, md: 3 },
            borderBottomLeftRadius: { xs: 0, md: 3 },
            borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "-32px 0 80px rgba(15, 23, 42, 0.26)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.25,
              bgcolor: "rgba(255,255,255,0.94)",
              backdropFilter: "blur(14px)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
                <IconButton onClick={handleCloseEditor} sx={{ flexShrink: 0 }}>
                  <CloseRounded />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    noWrap
                    color="text.primary"
                  >
                    {selectedQuestionnaireDetail?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Editor de cuestionario
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                startIcon={<SaveRounded />}
                onClick={() => void handleSaveSelectedQuestionnaire()}
                disabled={saving}
                loading={saving}
                sx={{
                  borderRadius: 3,
                  px: 2,
                  boxShadow: `0 14px 24px ${alpha("#032B66", 0.18)}`,
                }}
              >
                Guardar
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: { xs: 2, md: 3 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                mb: 2,
                borderRadius: 3,
                border: "1px solid rgba(15, 23, 42, 0.08)",
                bgcolor: "#ffffff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                useFlexGap
                flexWrap="wrap"
              >
                <Button
                  variant="outlined"
                  startIcon={<PreviewRounded />}
                  onClick={() => setPreviewOpen(true)}
                  sx={{
                    flex: 1,
                    minWidth: 140,
                    borderRadius: 2.5,
                    py: 1.05,
                    fontWeight: 700,
                  }}
                >
                  Preview
                </Button>
              </Stack>
            </Paper>

            <Tabs
              value={activeTab}
              onChange={(_, value: QuestionnaireTab) => setActiveTab(value)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                minHeight: 42,
                "& .MuiTab-root": {
                  minHeight: 42,
                  textTransform: "none",
                  fontWeight: 700,
                  minWidth: 90,
                },
              }}
            >
              <Tab label="General" value="general" />
              <Tab label="Versiones" value="versions" />
              <Tab label="Planes" value="plans" />
            </Tabs>
            <Divider sx={{ mb: 2 }} />

            {activeTab === "general" && (
              <Stack spacing={2.25}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    label="Nombre del Cuestionario"
                    value={editorName}
                    fullWidth
                    onChange={(event) => setEditorName(event.target.value)}
                  />
                  <TextField
                    label="Código Identificador"
                    value={editorCode}
                    fullWidth
                    onChange={(event) => setEditorCode(event.target.value)}
                  />
                </Stack>

                <TextField
                  label="Descripción"
                  value={editorDescription}
                  fullWidth
                  multiline
                  minRows={4}
                  onChange={(event) => setEditorDescription(event.target.value)}
                />

                <Card sx={pageCardSx}>
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800}>
                          Estado
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Define si el cuestionario está activo o inactivo.
                        </Typography>
                      </Box>

                      <Select
                        size="small"
                        value={editorStatus}
                        onChange={(event) =>
                          setEditorStatus(event.target.value as Questionnaire["status"])
                        }
                        sx={{ minWidth: 150, borderRadius: 2 }}
                      >
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                      </Select>
                    </Stack>
                  </CardContent>
                </Card>

      <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          Editor de cuestionario
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Abre el formulario embebido en grande para editarlo con
                          comodidad.
                        </Typography>
                      </Box>
                      <Chip
                        label={versionLabel(selectedVersion)}
                        color={
                          selectedVersion
                            ? statusColor(selectedVersion.status)
                            : "default"
                        }
                        variant="outlined"
                        sx={{ borderRadius: 999 }}
                      />
                    </Stack>

                    {selectedVersion ? (
                      <>
                        <Alert
                          severity={isDraftVersion ? "info" : "warning"}
                          sx={{ mb: 0 }}
                        >
                          {isDraftVersion
                            ? "Edita el contenido y guarda dentro del formulario. Al guardar, esta versión quedará publicada automáticamente."
                            : "Al editar se creará un borrador nuevo desde esta versión y se publicará automáticamente cuando guardes."}
                        </Alert>

                        <Button
                          variant="contained"
                          startIcon={<LaunchRounded />}
                          onClick={() => void handleOpenBuilder()}
                          sx={{
                            alignSelf: "flex-start",
                            borderRadius: 3,
                            px: 2.5,
                            py: 1.05,
                            boxShadow: `0 14px 24px ${alpha("#032B66", 0.16)}`,
                          }}
                        >
                          Editar contenido
                        </Button>
                      </>
                    ) : (
                      <Alert severity="info">
                        Todavía no existe una versión para este cuestionario.
                      </Alert>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}

            {activeTab === "versions" && (
              <Stack spacing={2.25}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Versiones
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Crea y administra el historial del cuestionario.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    onClick={() =>
                      createVersion({
                        questionnaireId: Number(selectedQuestionnaireDetail?.id),
                        sourceVersionId: selectedVersion
                          ? Number(selectedVersion.id)
                          : null,
                        schema: selectedVersionSchema,
                        notes: selectedVersion?.notes ?? undefined,
                      })
                    }
                    disabled={saving || !selectedQuestionnaireDetail}
                    sx={{ borderRadius: 3 }}
                  >
                    Nueva versión
                  </Button>
                </Stack>

                <Stack spacing={1.25}>
                  {questionnaireVersions.length === 0 ? (
                    <Alert severity="info">
                      Este cuestionario aún no tiene versiones creadas.
                    </Alert>
                  ) : (
                    questionnaireVersions.map((version) => {
                      const isSelected = version.id === selectedVersion?.id;

                      return (
                        <Card
                          key={version.id}
                          onClick={() => setVersion(version.id)}
                          sx={{
                            ...pageCardSx,
                            cursor: "pointer",
                            borderColor: isSelected
                              ? "primary.main"
                              : "rgba(15, 23, 42, 0.12)",
                            bgcolor: isSelected ? "#f8fbff" : "#ffffff",
                            boxShadow: isSelected
                              ? `0 18px 42px ${alpha("#032B66", 0.12)}`
                              : pageCardSx.boxShadow,
                          }}
                        >
                          <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={2}
                            >
                              <Box>
                                <Typography fontWeight={800}>
                                  Versión {version.versionNumber}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {version.status}
                                  {version.isActive ? " · activa" : ""}
                                </Typography>
                              </Box>

                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setVersion(version.id);
                                    setPreviewOpen(true);
                                  }}
                                >
                                  <PreviewRounded fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleEditVersion(version);
                                  }}
                                >
                                  <EditRounded fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </Stack>
              </Stack>
            )}

            {activeTab === "plans" && (
              <Stack spacing={2.25}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    Planes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Asigna los planes que pueden acceder a este cuestionario.
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Autocomplete
                    multiple
                    options={planOptions}
                    value={planOptions.filter((plan: Plan) =>
                      editorPlanCodes.includes(plan.code),
                    )}
                    getOptionLabel={(option) => option.name}
                    onChange={(_, value) => {
                      setEditorPlanCodes(value.map((plan) => plan.code));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Planes"
                        placeholder="Seleccionar"
                      />
                    )}
                  />

                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                    {editorPlanCodes.length > 0 ? (
                      editorPlanCodes.map((planCode) => (
                        <Chip
                          key={planCode}
                          label={planCode}
                          size="small"
                          sx={{ borderRadius: 999 }}
                        />
                      ))
                    ) : (
                      <Chip
                        label="Sin planes asignados"
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 999 }}
                      />
                    )}
                  </Stack>

                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    onClick={() =>
                      void assignPlans({
                        questionnaireId: Number(selectedQuestionnaireDetail?.id),
                        planCodes: editorPlanCodes,
                      })
                    }
                    disabled={saving || !selectedQuestionnaireDetail}
                    loading={saving}
                  >
                    Guardar planes
                  </Button>
                </Paper>
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Nuevo cuestionario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Nombre"
              value={questionnaireName}
              onChange={(event) => setQuestionnaireName(event.target.value)}
              fullWidth
            />
            <TextField
              label="Código"
              value={questionnaireCode}
              onChange={(event) => setQuestionnaireCode(event.target.value)}
              fullWidth
              helperText="Opcional. Si lo dejas vacío, se genera desde el nombre."
            />
            <TextField
              label="Descripción"
              value={questionnaireDescription}
              onChange={(event) => setQuestionnaireDescription(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <Autocomplete
              multiple
              options={planOptions}
              value={planOptions.filter((plan) =>
                selectedPlanCodes.includes(plan.code),
              )}
              getOptionLabel={(option) => option.name}
              onChange={(_, value) => setSelectedPlanCodes(value.map((plan) => plan.code))}
              renderInput={(params) => <TextField {...params} label="Planes" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => void handleCreate()}
            disabled={saving || !questionnaireName.trim()}
            loading={saving}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullScreen
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
            bgcolor: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(14px)",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              {selectedQuestionnaireDetail?.name || "Vista previa"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preview del cuestionario
            </Typography>
          </Box>

          <IconButton onClick={() => setPreviewOpen(false)}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: "#eef3ff" }}>
          {selectedQuestionnaireDetail && selectedVersion ? (
            <QuestionnaireEmbedFrame
              key={`preview-${selectedVersion.id}`}
              path={FORM_USER_URL}
              mode="preview"
              questionnaireCode={selectedQuestionnaireDetail.code}
              versionId={selectedVersion.id}
              readOnly
              closable
              chrome="compact"
              height="calc(100vh - 88px)"
              onClose={() => setPreviewOpen(false)}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">Selecciona un cuestionario y una versión para previsualizarlo.</Alert>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setBuilderVersionId(null);
        }}
        fullScreen
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
            bgcolor: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(14px)",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              {selectedQuestionnaireDetail?.name || "Editor de cuestionario"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeBuilderVersion
                ? `Versión ${activeBuilderVersion.versionNumber} · ${activeBuilderVersion.status}`
                : "Abrir editor embebido"}
            </Typography>
          </Box>

          <IconButton
            onClick={() => {
              setBuilderOpen(false);
              setBuilderVersionId(null);
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: { xs: 1.5, md: 2.5 },
            bgcolor: "#eef3ff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {selectedQuestionnaireDetail &&
          activeBuilderVersion &&
          activeBuilderVersion.status === "DRAFT" ? (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <QuestionnaireEmbedFrame
                key={`builder-${activeBuilderVersion.id}`}
                path={FORM_ADMIN_URL}
                mode="builder"
                questionnaireCode={selectedQuestionnaireDetail.code}
                versionId={activeBuilderVersion.id}
                readOnly={false}
                closable={false}
                height="100%"
                onSaved={(payload) => {
                  const versionId = activeBuilderVersion?.id;

                  if (
                    payload?.schema &&
                    selectedQuestionnaireDetail?.id &&
                    versionId
                  ) {
                    applySavedVersionSchema(
                      selectedQuestionnaireDetail.id,
                      versionId,
                      payload.schema,
                    );
                  }

                  if (versionId) {
                    void publishVersion(Number(versionId)).then((published) => {
                      if (published) {
                        setBuilderOpen(false);
                        setBuilderVersionId(null);
                      }
                    });
                  }
                }}
              />
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                Selecciona una versión DRAFT para editar el formulario.
              </Alert>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {saving && (
        <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1400 }}>
          <Chip label="Guardando..." color="primary" />
        </Box>
      )}
    </>
  );
}
